import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Modal.scss';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
}

const Modal: React.FC<ModalProps> = ({
                                       isOpen,
                                       onClose,
                                       title,
                                       children,
                                       size = 'medium',
                                       showCloseButton = true,
                                       closeOnOverlayClick = true,
                                       closeOnEscape = true,
                                     }) => {
  // Закрытие по ESC
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Блокировка скролла body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  const CloseIcon: React.FC = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const modalContent = (
    <div className="Modal__overlay" onClick={handleOverlayClick}>
      <div className={`Modal__container Modal__container--${size}`}>
        {(title || showCloseButton) && (
          <div className="Modal__header">
            {title && <h2 className="Modal__title">{title}</h2>}
            {showCloseButton && (
              <button
                className="Modal__close-button"
                onClick={onClose}
                aria-label="Close modal"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}

        <div className="Modal__content">
          {children}
        </div>
      </div>
    </div>
  );

  // Render в portal
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default Modal;